<script setup>
import {onMounted, ref} from "vue";
import Button from "../../components/forms/Button.vue";
import {useRouter} from "vue-router";
import {formatDate, showSuccessMessage} from "@/helper";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import {getLoggedUser} from "@/services/AuthService";
import CreateUpdateStrategyModal from "@/components/modals/CreateUpdateStrategyModal.vue";
import StrategyService from "@/services/StrategyService";
import {useI18n} from "vue-i18n";
import ContractsIndexTableComponent from "@/components/clients/ContractsIndexTableComponent.vue";
import StrategyBondsCard from "@/components/bonds/StrategyBondsCard.vue";
import StrategyBondsSummaryCard from "@/components/bonds/StrategyBondsSummaryCard.vue";

const {t} = useI18n();
const router = useRouter();
const strategyId = router.currentRoute.value.params.id;
const strategy = ref({});
const isLoading = ref(true);
const isEditable = ref(false);

onMounted(() => {
  fetchStrategy();
  getLoggedUser().then(response => {
    let loggedUser = response;
    if (loggedUser.role === 'ADMIN' || loggedUser.role === 'SUPERADMIN') {
      isEditable.value = true;
    }
  });
});

function getArrayLabel(input, prefix = null) {
  // Returns a comma-separated string of translated labels for an array or single value.
  // If input is falsy or empty, returns '---'.
  if (input === null || input === undefined) return '---';

  const translate = (val) => {
    if (val === null || val === undefined || val === '') return '';
    if (prefix) {
      val = prefix + val;
    }
    const key = `strategiesDetail.${val}`;
    const translated = t(key);
    // vue-i18n returns the key itself if translation does not exist; handle fallback
    return translated && translated !== key ? translated : String(val);
  };

  if (Array.isArray(input)) {
    if (input.length === 0) return '---';
    return input.map(translate).filter(Boolean).join(', ');
  }

  // Handle single non-array value
  const single = translate(input);
  return single || '---';
}

function fetchStrategy() {
  isLoading.value = true;
  StrategyService.getStrategy(strategyId).then((response) => {
    strategy.value = response;
    isLoading.value = false;
  });
}

function updateStrategy() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateUpdateStrategyModal, strategy.value ? {
    strategy: strategy.value,
  } : {});
  reveal();
  onConfirm((data) => {
    StrategyService.saveStrategy(data, strategyId).then(response => {
      showSuccessMessage(response.message);
      strategy.value = response.strategy;
    });
  });
  onCancel(() => {
  });
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('strategiesDetail.back') }}</a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">
            {{ t('strategiesDetail.strategy_detail') }}
          </div>
        </div>
        <div class="col-4 d-flex justify-content-end">
        </div>
      </div>

    </div>
    <div class="page-section__content">
      <div class="mb-4">
        <div class="row">
          <div class="col-12 col-md-6">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('strategiesDetail.basic_info') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button v-if="isEditable" :label="t('strategiesDetail.edit')" @click="updateStrategy()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="info-table">
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.name') }}</span>
                    <span class="info-table__value col-12">{{ strategy.strategy_name ?? '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.code') }}</span>
                    <span class="info-table__value col-12">{{ strategy.code ?? '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.external_id') }}</span>
                    <span class="info-table__value col-12">{{ strategy.external_id && strategy.external_id !== "" ? strategy.external_id : '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.active') }}</span>
                    <span class="info-table__value col-12">{{ strategy.is_active ? t('strategies.yes') : t('strategies.no') }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.description') }}</span>
                    <span class="info-table__value col-12">{{ strategy.description ?? '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.performance') }}</span>
                    <span class="info-table__value col-12">{{ strategy?.performance === null || strategy?.performance === undefined ? '---' : (Number(strategy.performance * 100).toFixed(2) + ' %') }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.risk_profile') }}</span>
                    <span class="info-table__value col-12">{{ strategy?.risk_profile ?? '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.active_for_all_contract_types') }}</span>
                    <span class="info-table__value col-12">{{ strategy.is_active_all_contract_types ? 'Ano' : 'Ne' }} {{ strategy.is_active_all_contract_types_from || strategy.is_active_all_contract_types_from ? '(' + (strategy.is_active_all_contract_types_from ? 'Od: ' + formatDate(strategy.is_active_all_contract_types_from, 'd.m.Y H:i')  + ', ' : '') + (strategy.is_active_all_contract_types_to ? 'Do: ' + formatDate(strategy.is_active_all_contract_types_to, 'd.m.Y H:i') + ')' : '') : ''}}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.contract_types') }}</span>
                    <span class="info-table__value col-12">{{ strategy.contract_types_names ?? '---' }}</span>
                  </div>
<!--                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.target_market') }}:</span>
                  </div>-->
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.client_type') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.client_type) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.adequacy_test') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.adequacy_test) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.ability_to_bear_loss') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.ability_to_bear_loss) }}</span>
                  </div>
<!--                  <div class="info-table__row row">
                    <span class="info-table__label col-12">Rizikovost:</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.client_risk) }}</span>
                  </div>-->
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.risk_tolerance_correction') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.risk_tolerance_correction, 'RISK_TOLERANCE_CORRECTION_') ?? '---' }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.investment_goals') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.client_investment_goals) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.investment_preferences') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.investment_preferences) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.investment_horizon') }}</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.investment_horizon) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">ESG</span>
                    <span class="info-table__value col-12">{{ getArrayLabel(strategy.client_esg) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('strategiesDetail.intermediaries_can_sell') }}</span>
                    <span class="info-table__value col-12">{{ strategy.for_intermediaries_label ?? t('strategiesDetail.no_restrictions') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- IMA-517: strategie Dluhopisy – souhrn vedle základních informací -->
          <div v-if="strategy.product_type === 'BOND'" class="col-12 col-md-6 mt-3 mt-md-0">
            <StrategyBondsSummaryCard :strategy-id="strategyId"/>
          </div>
        </div>
      </div>
      <!-- IMA-517: tabulka dluhopisů až pod horními kartami, přes celou šířku (pod strategii patří všechny dluhopisy) -->
      <div v-if="strategy.product_type === 'BOND'" class="mb-4">
        <StrategyBondsCard/>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
